import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useTags, useCreateTag, useUpdateTag, useDeleteTag, useUpdateTagSetting } from '../../hooks/useTags';
import FileUploader from '../FileUploader';
import Modal from '../Modal';
import ConfirmationModal from '../ConfirmationModal';
import toast from 'react-hot-toast';
import { tagService } from '../../utils/api';

/**
 * TagTab component for managing tags in the settings page
 * 
 * @param {Object} props Component props
 * @param {number} props.schoolId The school ID
 * @param {Array} props.selectedLanguages List of languages selected for the school
 * @param {Object} props.schoolInfo School information including default language and show_tags setting
 */
const TagTab = ({ 
  schoolId, 
  selectedLanguages, 
  schoolInfo
}) => {
  console.log('[TagTab] Component rendering/re-rendering');
  console.log('[TagTab] Props received:', { schoolId, selectedLanguages, schoolInfo });
  
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language;
  
  // Get all tags for this school
  const { 
    data: tags = [], 
    isLoading: isTagsLoading, 
    isError: isTagsError,
    refetch: refetchTags
  } = useTags(schoolId, currentLanguage);
  
  // Mutation hooks
  const createTagMutation = useCreateTag();
  const updateTagMutation = useUpdateTag();
  const deleteTagMutation = useDeleteTag();
  const updateTagSettingMutation = useUpdateTagSetting();
  
  // State for the show_tags setting
  const [showTags, setShowTags] = useState(schoolInfo?.show_tags || false);
  const [isSavingTagSetting, setIsSavingTagSetting] = useState(false);
  
  // Add debug logs
  console.log('[TagTab] Initial schoolInfo:', schoolInfo);
  console.log('[TagTab] Initial showTags state:', showTags);
  
  // State for new tag form
  const [newTag, setNewTag] = useState({
    translations: {},
    logo_data: null,
    logoPreview: null
  });
  
  // State for edit modal
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [tagToEdit, setTagToEdit] = useState(null);
  const [isFetchingEditData, setIsFetchingEditData] = useState(false);
  
  // State for delete modal
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [tagToDelete, setTagToDelete] = useState(null);
  
  // Update local state when school setting changes
  useEffect(() => {
    if (schoolInfo) {
      console.log('[TagTab] schoolInfo changed:', schoolInfo);
      console.log('[TagTab] schoolInfo.show_tags:', schoolInfo.show_tags);
      setShowTags(schoolInfo.show_tags || false);
    }
  }, [schoolInfo]);
  
  // Handle saving the tag display setting
  const handleSaveTagSetting = async () => {
    if (!schoolId) {
      toast.error(t('tags.noSchoolId', 'School ID is missing'));
      return;
    }
    
    setIsSavingTagSetting(true);
    console.log('[TagTab] Saving tag setting. Current state value:', showTags);
    
    try {
      await updateTagSettingMutation.mutateAsync({
        schoolId,
        showTags
      });
      
      toast.success(t('settings.settingsSaved', 'Settings saved successfully'));
      console.log('[TagTab] Successfully saved tag setting. New value:', showTags);
    } catch (error) {
      console.error('Error updating tag setting:', error);
      toast.error(t('tags.settingUpdateError', 'Failed to update tag display setting'));
      // Revert to original value if save fails
      setShowTags(schoolInfo.show_tags || false);
    } finally {
      setIsSavingTagSetting(false);
    }
  };
  
  // Handle logo upload for new tag
  const handleLogoUpload = (file) => {
    if (!file) {
      setNewTag(prev => ({
        ...prev,
        logo_data: null,
        logoPreview: null
      }));
      return;
    }
    
    const reader = new FileReader();
    reader.onloadend = () => {
      setNewTag(prev => ({
        ...prev,
        logo_data: reader.result,
        logoPreview: reader.result
      }));
    };
    reader.readAsDataURL(file);
  };
  
  // Handle logo upload for edit modal
  const handleEditLogoUpload = (file) => {
    if (!file) {
      setTagToEdit(prev => ({
        ...prev,
        logo_data: null,
        logoPreview: null,
        remove_logo: true
      }));
      return;
    }
    
    const reader = new FileReader();
    reader.onloadend = () => {
      setTagToEdit(prev => ({
        ...prev,
        logo_data: reader.result,
        logoPreview: reader.result,
        remove_logo: false
      }));
    };
    reader.readAsDataURL(file);
  };
  
  // Handle translation changes for new tag
  const handleTranslationChange = (langCode, value) => {
    console.log('[TagTab] handleTranslationChange called:', { langCode, value });
    setNewTag(prev => {
      const newState = {
        ...prev,
        translations: {
          ...prev.translations,
          [langCode]: value
        }
      };
      console.log('[TagTab] Updated newTag state:', newState);
      return newState;
    });
  };
  
  // Handle editing translation changes
  const handleEditTranslationChange = (langCode, value) => {
    setTagToEdit(prev => ({
      ...prev,
      translations: {
        ...prev.translations,
        [langCode]: value
      }
    }));
  };
  
  // Open edit modal for a tag
  const openEditModal = (tag) => {
    if (!schoolId) {
      toast.error(t('tags.noSchoolId', 'School ID is missing'));
      return;
    }
    
    // First set the basic information
    setTagToEdit({
      id: tag.id,
      translations: {}, // Will be populated from the backend response
      logoPreview: tag.logo_path ? getLogoUrl(tag.logo_path) : null,
      logo_path: tag.logo_path
    });
    
    setIsFetchingEditData(true);
    setEditModalOpen(true);
    
    // Fetch full tag data including all translations
    fetchTagDetails(tag.id);
  };
  
  // Fetch detailed tag data including all translations
  const fetchTagDetails = async (tagId) => {
    try {
      // Use the tagService to get the specific tag
      const tag = await tagService.getTag(tagId, schoolId);
      
      // Convert translations array to object for easier form handling
      const translationsObj = {};
      if (tag.translations && Array.isArray(tag.translations)) {
        tag.translations.forEach(trans => {
          translationsObj[trans.language_code] = trans.name;
        });
      }
      
      setTagToEdit(prev => ({
        ...prev,
        translations: translationsObj
      }));
    } catch (error) {
      console.error('Error fetching tag details:', error);
      toast.error(t('tags.fetchDetailError', 'Failed to fetch tag details'));
    } finally {
      setIsFetchingEditData(false);
    }
  };
  
  // Close edit modal
  const closeEditModal = () => {
    setEditModalOpen(false);
    setTagToEdit(null);
    setIsFetchingEditData(false);
  };
  
  // Open delete modal
  const openDeleteModal = (tag) => {
    setTagToDelete(tag);
    setDeleteModalOpen(true);
  };
  
  // Close delete modal
  const closeDeleteModal = () => {
    setDeleteModalOpen(false);
    setTagToDelete(null);
  };
  
  // Handle creating new tag
  const handleCreateTag = async (e) => {
    console.log('[TagTab] handleCreateTag function called - START');
    console.log('[TagTab] Event object:', e);
    console.log('[TagTab] Event type:', e?.type);
    console.log('[TagTab] Event target:', e?.target);
    
    e.preventDefault();
    console.log('[TagTab] preventDefault called successfully');
    
    if (!schoolId) {
      console.log('[TagTab] Early return - no schoolId');
      toast.error(t('tags.noSchoolId', 'School ID is missing'));
      return;
    }
    
    console.log('[TagTab] schoolId check passed:', schoolId);
    
    // Validate that at least the default language has a name
    const defaultLangCode = selectedLanguages.find(lang => lang.id === schoolInfo.defaultLanguageId)?.code;
    console.log('[TagTab] Validation - defaultLangCode:', defaultLangCode);
    console.log('[TagTab] Validation - schoolInfo.defaultLanguageId:', schoolInfo.defaultLanguageId);
    console.log('[TagTab] Validation - selectedLanguages:', selectedLanguages);
    console.log('[TagTab] Validation - newTag.translations:', newTag.translations);
    console.log('[TagTab] Validation - newTag.translations[defaultLangCode]:', newTag.translations[defaultLangCode]);
    
    if (!defaultLangCode || !newTag.translations[defaultLangCode]?.trim()) {
      console.log('[TagTab] Validation failed - returning early');
      toast.error(t('tags.nameRequired', 'Tag name is required for the default language'));
      return;
    }
    
    try {
      // Prepare tag data
      console.log('[TagTab] newTag.translations:', newTag.translations);
      
      const filteredTranslations = Object.entries(newTag.translations)
        .filter(([_, name]) => name && name.trim())
        .map(([language_code, name]) => ({ language_code, name: name.trim() }));
      
      const tagData = {
        translations: filteredTranslations,
        logo_data: newTag.logo_data
      };
      
      console.log('[TagTab] Prepared tagData:', tagData);
      console.log('[TagTab] Mutation parameters:', { schoolId, tag: tagData });
      
      console.log('[TagTab] About to call createTagMutation.mutateAsync...');
      console.log('[TagTab] createTagMutation object:', createTagMutation);
      console.log('[TagTab] createTagMutation.mutateAsync type:', typeof createTagMutation.mutateAsync);
      
      await createTagMutation.mutateAsync({ schoolId, tag: tagData });
      
      console.log('[TagTab] Mutation call completed successfully');
      
      // Reset form
      setNewTag({
        translations: {},
        logo_data: null,
        logoPreview: null
      });
      
      toast.success(t('tags.created', 'Tag created successfully'));
      
      // Refetch tags to update the list
      refetchTags();
    } catch (error) {
      console.error('Error creating tag:', error);
      toast.error(t('tags.createError', 'Failed to create tag'));
    }
  };
  
  // Handle updating tag
  const handleUpdateTag = async (e) => {
    e.preventDefault();
    
    if (!tagToEdit?.id) return;
    
    // Validate that at least the default language has a name
    const defaultLangCode = selectedLanguages.find(lang => lang.id === schoolInfo.defaultLanguageId)?.code;
    if (!defaultLangCode || !tagToEdit.translations[defaultLangCode]?.trim()) {
      toast.error(t('tags.nameRequired', 'Tag name is required for the default language'));
      return;
    }
    
    try {
      // Prepare update data
      const updateData = {
        translations: Object.entries(tagToEdit.translations)
          .filter(([_, name]) => name && name.trim())
          .map(([language_code, name]) => ({ language_code, name: name.trim() })),
        logo_data: tagToEdit.logo_data,
        remove_logo: tagToEdit.remove_logo || false
      };
      
      await updateTagMutation.mutateAsync({ tagId: tagToEdit.id, tag: updateData });
      
      toast.success(t('tags.updated', 'Tag updated successfully'));
      closeEditModal();
      
      // Refetch tags to update the list
      refetchTags();
    } catch (error) {
      console.error('Error updating tag:', error);
      toast.error(t('tags.updateError', 'Failed to update tag'));
    }
  };
  
  // Handle deleting tag
  const handleDeleteTag = async () => {
    if (!tagToDelete?.id) return;
    
    try {
      await deleteTagMutation.mutateAsync(tagToDelete.id);
      toast.success(t('tags.deleted', 'Tag deleted successfully'));
      closeDeleteModal();
      
      // Refetch tags to update the list
      refetchTags();
    } catch (error) {
      console.error('Error deleting tag:', error);
      toast.error(t('tags.deleteError', 'Failed to delete tag'));
    }
  };
  
  // Get best translation for a tag
  const getBestTranslation = (tag) => {
    if (!tag) return '';
    
    // First try current UI language
    if (tag.name && currentLanguage) {
      return tag.name;
    }
    
    // Then try default language
    const defaultLangCode = selectedLanguages.find(lang => lang.id === schoolInfo.defaultLanguageId)?.code;
    if (defaultLangCode && tag.translations && Array.isArray(tag.translations)) {
      const defaultTranslation = tag.translations.find(t => t.language_code === defaultLangCode);
      if (defaultTranslation) {
        return defaultTranslation.name;
      }
    }
    
    // Fall back to first available translation
    if (tag.translations && Array.isArray(tag.translations) && tag.translations.length > 0) {
      return tag.translations[0].name;
    }
    
    return t('tags.noTranslation', 'No translation available');
  };
  
  // Helper function to get logo URL
  const getLogoUrl = (logoPath) => {
    if (!logoPath) return null;
    
    // If the path already has a protocol, return it as is
    if (logoPath.startsWith('http')) return logoPath;
    
    // Handle UUID format (Nhost storage)
    if (logoPath.includes('-') && !logoPath.includes('/')) {
      return `https://${process.env.REACT_APP_NHOST_SUBDOMAIN || 'gtwosmwmvopoqrodakmd'}.storage.${process.env.REACT_APP_NHOST_REGION || 'eu-central-1'}.nhost.run/v1/files/${logoPath}`;
    }
    
    // For relative paths with leading slash
    if (logoPath.startsWith('/')) {
      return `${window.location.origin}${logoPath}`;
    }
    
    // For legacy path format
    // Add cache-busting timestamp parameter to prevent caching issues
    const cacheBuster = `?t=${Date.now()}`;
    return `${window.location.origin}/api/uploads/logos/${logoPath.split('/').pop()}${cacheBuster}`;
  };
  
  // Main render
  return (
    <div className="space-y-6">
      {/* Display Settings Section */}
      <div className="bg-white p-6 rounded-lg shadow-sm">
        <h2 className="text-xl font-semibold">{t('tags.displaySettings', 'Tag Display Settings')}</h2>
        <p className="text-sm text-gray-600 mt-2">
          {t('tags.showInMenuHelp', 'When enabled, tag icons will appear under dishes in the public menu with a legend explaining each tag.')}
        </p>
        <div className="flex justify-between items-center">
          <div className="flex items-center">
            <input
              type="checkbox"
              id="show-tags-setting"
              checked={showTags}
              onChange={(e) => setShowTags(e.target.checked)}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="show-tags-setting" className="ml-2 block text-sm text-gray-700">
              {t('tags.showInMenu', 'Show tag information in public menu')}
            </label>
          </div>
          <button
            onClick={handleSaveTagSetting}
            disabled={isSavingTagSetting}
            className={`px-4 py-2 bg-blue-500 text-white rounded-md shadow hover:bg-blue-600 ${
              isSavingTagSetting ? 'opacity-70 cursor-not-allowed' : ''
            }`}
          >
            {isSavingTagSetting ? t('settings.saving', 'Saving...') : t('settings.saveChanges', 'Save Changes')}
          </button>
        </div>
      </div>
      
      {/* Header titles in a flex row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-4">
        <h2 className="text-xl font-semibold">{t('tags.existing', 'Existing Tags')}</h2>
        <h3 className="text-xl font-semibold">{t('tags.createNew', 'Create New Tag')}</h3>
      </div>
      
      {/* Horizontal layout container */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Tags list - left side */}
        <div className="space-y-2">
          {isTagsLoading ? (
            <div className="flex justify-center py-4">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
            </div>
          ) : isTagsError ? (
            <div className="p-4 bg-red-100 text-red-700 rounded mb-4">
              {t('tags.loadError', 'Error loading tags. Please try again later.')}
            </div>
          ) : tags.length === 0 ? (
            <div className="p-4 bg-gray-100 text-gray-700 rounded mb-4">
              {t('tags.empty', 'No tags have been created yet.')}
            </div>
          ) : (
            tags.map((tag) => {
              // Find best translation to display
              const defaultLangCode = selectedLanguages.find(lang => lang.id === schoolInfo.defaultLanguageId)?.code;
              
              let displayText = null;
              let displayLangCode = null;
              const translations = tag.translations || {};
  
              // Try current UI language first
              if (translations && tag.name) {
                displayText = tag.name;
                displayLangCode = currentLanguage;
              } 
              // Then try default language
              else if (defaultLangCode && translations && Array.isArray(translations)) {
                const defaultTranslation = translations.find(t => t.language_code === defaultLangCode);
                if (defaultTranslation) {
                  displayText = defaultTranslation.name;
                  displayLangCode = defaultLangCode;
                }
              }
              // Fall back to any available translation
              if (!displayText && translations && Array.isArray(translations) && translations.length > 0) {
                displayText = translations[0].name;
                displayLangCode = translations[0].language_code;
              }
              
              // Final fallback
              if (!displayText) {
                displayText = t('tags.noTranslation', 'No translation available');
                displayLangCode = null;
              }
              
              const isFallback = displayLangCode !== null && displayLangCode !== currentLanguage;
              const nameClass = 'mr-3 font-medium' + (isFallback ? ' italic' : '');
              
              return (
                <div key={tag.id} className="flex justify-between items-center p-3 bg-gray-50 rounded-md">
                  <div className="flex items-center flex-grow mr-4">
                    {/* Logo preview */}
                    {tag.logo_path ? (
                      <img 
                        src={getLogoUrl(tag.logo_path)} 
                        alt={displayText}
                        className="w-8 h-8 object-contain mr-3"
                      />
                    ) : (
                      <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center mr-3">
                        <span className="text-xs text-gray-500">
                          {t('tags.noLogo', 'No logo')}
                        </span>
                      </div>
                    )}
                    
                    {/* Tag name */}
                    <span className={nameClass}>
                      {displayText}
                      {/* Display language code in gray if it's a fallback */}
                      {isFallback && displayLangCode && (
                        <span className="text-xs ml-1 text-gray-400">
                          ({displayLangCode.toUpperCase()})
                        </span>
                      )}
                    </span>
                    
                    {/* Language Indicator Labels */}
                    <div className="flex space-x-1 flex-shrink-0">
                      {selectedLanguages.map(lang => {
                        // Check if this language has a translation
                        const hasTranslation = translations && Array.isArray(translations) 
                          ? translations.some(t => t.language_code === lang.code && t.name && t.name.trim() !== '')
                          : false;
                          
                        const title = hasTranslation 
                          ? t(`languageNames.${lang.code}`, lang.code) + ' (' + t('settings.translated', 'Translated') + ')' 
                          : t(`languageNames.${lang.code}`, lang.code) + ' (' + t('settings.noTranslation', 'No translation') + ')';
                        
                        return (
                          <span 
                            key={lang.code}
                            className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-semibold
                              ${hasTranslation ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-500'}`}
                            title={title}
                          >
                            {lang.code.toUpperCase()}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                  
                  {/* Action buttons */}
                  <div className="flex items-center">
                    <button
                      className="text-blue-500 hover:text-blue-700 mr-3"
                      onClick={() => openEditModal(tag)}
                    >
                      {t('common.edit', 'Edit')}
                    </button>
                    <button
                      className="text-red-500 hover:text-red-700"
                      onClick={() => openDeleteModal(tag)}
                    >
                      {t('common.delete', 'Delete')}
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
        
        {/* Add New Tag Form - right side */}
        <div className="bg-white p-4 rounded-lg shadow-sm">
          <form 
            onSubmit={(e) => {
              console.log('[TagTab] Form onSubmit triggered');
              console.log('[TagTab] Form onSubmit event:', e);
              console.log('[TagTab] About to call handleCreateTag');
              return handleCreateTag(e);
            }}
            className="space-y-4"
          >
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h4 className="font-medium text-gray-700">{t('tags.translations', 'Translations')}</h4>
              </div>
              {selectedLanguages.length > 0 ? (
                // Sort languages to show default language first, then others alphabetically
                [...selectedLanguages]
                  .sort((a, b) => {
                    // Put default language at the top
                    if (a.id === schoolInfo.defaultLanguageId) return -1;
                    if (b.id === schoolInfo.defaultLanguageId) return 1;
                    // Otherwise sort alphabetically by name
                    return a.name.localeCompare(b.name);
                  })
                  .map((lang) => {
                    const isDefaultLanguage = lang.id === schoolInfo.defaultLanguageId;
                    return (
                      <div key={lang.code} className="flex items-start">
                        <span className={`w-24 text-sm ${isDefaultLanguage ? 'font-semibold' : ''} mt-2`}>
                          {t(`languageNames.${lang.code}`, lang.code)} ({lang.code})
                          {isDefaultLanguage && <span className="text-red-500 ml-1">*</span>}
                        </span>
                        <div className="flex-grow relative">
                          <input
                            type="text"
                            name={`translation-${lang.code}`}
                            value={newTag.translations[lang.code] || ''}
                            onChange={(e) => handleTranslationChange(lang.code, e.target.value)}
                            placeholder={isDefaultLanguage ? 
                              t('settings.requiredTranslation', 'Required translation') : 
                              t('settings.enterTranslation', 'Enter translation')
                            }
                            className={`flex-grow border ${isDefaultLanguage ? 'border-blue-300' : 'border-gray-300'} rounded-md shadow-sm p-2 w-full
                              ${isDefaultLanguage ? 'bg-blue-50' : ''}`}
                            required={isDefaultLanguage}
                          />
                          {isDefaultLanguage && (
                            <span className="text-xs text-blue-600 mt-1 block">
                              {t('settings.defaultLanguageField', 'Default language (required)')}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })
              ) : (
                <p className="text-sm text-gray-500">{t('tags.noLanguages', 'No languages selected for this school. Please add languages in the Languages tab.')}</p>
              )}
            </div>
            
            {/* Logo upload section */}
            <div className="mt-4">
              <h4 className="font-medium text-gray-700 mb-2">{t('tags.logo', 'Tag Logo')}</h4>
              
              <FileUploader
                label={t('tags.uploadLogo', 'Upload logo (optional)')}
                accept="image/*"
                acceptedFormats={['.jpg', '.jpeg', '.png', '.gif', '.svg', '.webp']}
                maxSizeMB={2}
                onFileSelected={handleLogoUpload}
                defaultPreview={newTag.logoPreview}
              />
              
              <p className="mt-2 text-xs text-gray-500">
                {t('tags.logoRequirements', 'Recommended size: 64x64px. Max size: 2MB. Supported formats: JPG, PNG, GIF, SVG, WebP')}
              </p>
            </div>
            
            {/* Submit button */}
            <div className="mt-4 flex justify-end">
              <button
                type="submit"
                className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
                disabled={createTagMutation.isPending || selectedLanguages.length === 0}
              >
                {createTagMutation.isPending ? (
                  <div className="flex items-center">
                    <div className="animate-spin mr-2 h-4 w-4 border-t-2 border-b-2 border-white rounded-full"></div>
                    {t('tags.creating', 'Creating...')}
                  </div>
                ) : (
                  t('tags.create', 'Create Tag')
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
      
      {/* Edit Modal */}
      <Modal
        isOpen={editModalOpen}
        onClose={closeEditModal}
        title={t('tags.editTag', 'Edit Tag')}
      >
        {isFetchingEditData ? (
          <div className="flex justify-center items-center h-40">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        ) : tagToEdit ? (
          <form onSubmit={handleUpdateTag} className="space-y-4">
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h4 className="font-medium text-gray-700">{t('tags.translations', 'Translations')}</h4>
              </div>
              {selectedLanguages.length > 0 ? (
                [...selectedLanguages]
                  .sort((a, b) => {
                    if (a.id === schoolInfo.defaultLanguageId) return -1;
                    if (b.id === schoolInfo.defaultLanguageId) return 1;
                    return a.name.localeCompare(b.name);
                  })
                  .map((lang) => {
                    const isDefaultLanguage = lang.id === schoolInfo.defaultLanguageId;
                    return (
                      <div key={lang.code} className="flex items-start">
                        <span className={`w-24 text-sm ${isDefaultLanguage ? 'font-semibold' : ''} mt-2`}>
                          {t(`languageNames.${lang.code}`, lang.code)} ({lang.code})
                          {isDefaultLanguage && <span className="text-red-500 ml-1">*</span>}
                        </span>
                        <div className="flex-grow relative">
                          <input
                            type="text"
                            name={`edit-translation-${lang.code}`}
                            value={tagToEdit.translations[lang.code] || ''}
                            onChange={(e) => handleEditTranslationChange(lang.code, e.target.value)}
                            placeholder={isDefaultLanguage ? 
                              t('settings.requiredTranslation', 'Required translation') : 
                              t('settings.enterTranslation', 'Enter translation')
                            }
                            className={`flex-grow border ${isDefaultLanguage ? 'border-blue-300' : 'border-gray-300'} rounded-md shadow-sm p-2 w-full
                              ${isDefaultLanguage ? 'bg-blue-50' : ''}`}
                            required={isDefaultLanguage}
                          />
                          {isDefaultLanguage && (
                            <span className="text-xs text-blue-600 mt-1 block">
                              {t('settings.defaultLanguageField', 'Default language (required)')}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })
              ) : (
                <p className="text-sm text-gray-500">{t('tags.noLanguages', 'No languages selected for this school.')}</p>
              )}
            </div>
            
            {/* Logo upload section */}
            <div className="mt-4">
              <h4 className="font-medium text-gray-700 mb-2">{t('tags.logo', 'Tag Logo')}</h4>
              
              <FileUploader
                label={t('tags.uploadLogo', 'Upload logo (optional)')}
                accept="image/*"
                acceptedFormats={['.jpg', '.jpeg', '.png', '.gif', '.svg', '.webp']}
                maxSizeMB={2}
                onFileSelected={handleEditLogoUpload}
                defaultPreview={tagToEdit.logoPreview}
              />
              
              <p className="mt-2 text-xs text-gray-500">
                {t('tags.logoRequirements', 'Recommended size: 64x64px. Max size: 2MB. Supported formats: JPG, PNG, GIF, SVG, WebP')}
              </p>
            </div>
            
            {/* Action buttons */}
            <div className="mt-6 flex justify-end space-x-3">
              <button
                type="button"
                onClick={closeEditModal}
                className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded"
              >
                {t('common.cancel', 'Cancel')}
              </button>
              <button
                type="submit"
                className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
                disabled={updateTagMutation.isPending}
              >
                {updateTagMutation.isPending ? (
                  <div className="flex items-center">
                    <div className="animate-spin mr-2 h-4 w-4 border-t-2 border-b-2 border-white rounded-full"></div>
                    {t('common.saving', 'Saving...')}
                  </div>
                ) : (
                  t('common.save', 'Save')
                )}
              </button>
            </div>
          </form>
        ) : null}
      </Modal>
      
      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={deleteModalOpen}
        onClose={closeDeleteModal}
        title={t('tags.deleteConfirmation', 'Delete Tag')}
      >
        {tagToDelete && (
          <div className="p-4">
            <p className="text-gray-700 mb-4">
              {t('tags.deleteWarning', 'Are you sure you want to delete this tag? This action cannot be undone.')}
            </p>
            <p className="font-semibold mb-6">
              {getBestTranslation(tagToDelete)}
            </p>
            
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={closeDeleteModal}
                className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded"
              >
                {t('common.cancel', 'Cancel')}
              </button>
              <button
                type="button"
                onClick={handleDeleteTag}
                className="bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded"
                disabled={deleteTagMutation.isPending}
              >
                {deleteTagMutation.isPending ? (
                  <div className="flex items-center">
                    <div className="animate-spin mr-2 h-4 w-4 border-t-2 border-b-2 border-white rounded-full"></div>
                    {t('settings.deleting', 'Deleting...')}
                  </div>
                ) : (
                  t('settings.delete', 'Delete')
                )}
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default TagTab; 